import React from 'react';
import { useNavigate } from 'react-router-dom';
import StageProgressBar from './StageProgressBar';
import { BusinessIdea } from './onboarding/onboarding.types';
import { Stage } from '@orbit/shared/constants';
import { ChevronRight } from '@mui/icons-material';
import { Tooltip } from '@mui/material';

interface BusinessIdeaCardProps {
  businessIdea: BusinessIdea;
  currentStage?: Stage;
  className?: string;
}

const BusinessIdeaCard: React.FC<BusinessIdeaCardProps> = ({
  businessIdea,
  currentStage = Stage.Explore,
  className = '',
}) => {
  const navigate = useNavigate();

  // Map business idea stage to our Stage enum
  const getStageFromBusinessIdea = (stage: string): Stage => {
    switch (stage) {
      case 'idea':
      case 'validation':
        return Stage.Explore;
      case 'mvp':
        return Stage.Incubation;
      case 'launched':
        return Stage.Launch;
      case 'scaling':
        return Stage.Growth;
      default:
        return Stage.Explore;
    }
  };

  const mappedStage = getStageFromBusinessIdea(businessIdea.stage);

  const handleCardClick = () => {
    navigate(`/business-idea/${businessIdea.id}`, {
      state: { businessIdea },
    });
  };

  return (
    <div
      className={`bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 overflow-hidden cursor-pointer transition-all duration-200 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 hover:scale-[1.02] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 group ${className}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      {/* Card Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-xl font-bold font-heading text-white truncate">
            {businessIdea.name}
          </h2>
          <ChevronRight className="text-text-secondary group-hover:text-blue-400 ml-2 flex-shrink-0 transition-all duration-200 group-hover:translate-x-1" />
        </div>

        {/* Summary */}
        <p className="text-text-secondary text-sm line-clamp-2 mb-4">{businessIdea.summary}</p>

        {/* Industries */}
        {businessIdea.industry && businessIdea.industry.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {businessIdea.industry.slice(0, 3).map((industry, index) => (
              <span key={index} className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                {industry}
              </span>
            ))}
            {businessIdea.industry.length > 3 && (
              <Tooltip title={businessIdea.industry.slice(3).join(', ')} arrow placement="top">
                <span className="text-xs text-text-secondary px-2 py-1 cursor-help">
                  +{businessIdea.industry.length - 3} more
                </span>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      {/* Stage Progress Bar */}
      <div className="px-6 pb-6 mt-6">
        <h3 className="text-lg font-bold font-heading text-white truncate mb-4">Progress</h3>
        <StageProgressBar highlightStage={mappedStage} />
      </div>

      {/* Click indicator */}
      <div className="px-6 pb-4">
        <div className="flex items-center justify-center text-xs text-text-secondary group-hover:text-primary transition-colors duration-200">
          <span>Click to view details</span>
        </div>
      </div>
    </div>
  );
};

export default BusinessIdeaCard;
